import React from 'react';
import { Link } from 'react-scroll';

export const Hero = () => {
  return (
    <section className="relative min-h-screen flex flex-col items-center justify-start overflow-hidden pt-28 pb-20 bg-gradient-to-b from-slate-50 via-white to-slate-50/50">
      {/* Ambient background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-turret-purple/8 rounded-full blur-[100px]" />
        <div className="absolute top-1/3 -right-20 w-[400px] h-[400px] bg-indigo-400/6 rounded-full blur-[80px]" />
        <div className="absolute bottom-0 left-1/3 w-[600px] h-[300px] bg-turret-purple-light/5 rounded-full blur-[100px]" />
      </div>

      {/* Subtle grid overlay */}
      <div 
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6">
        {/* Hero text content */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-turret-purple/5 border border-turret-purple/10 mb-8">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-turret-purple opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-turret-purple"></span>
            </span>
            <span className="text-sm font-medium text-turret-purple">Now in public beta</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight mb-6">
            <span className="text-neutral-900">Conversation analytics</span>
            <br />
            <span className="gradient-text">for AI products</span>
          </h1>

          <p className="text-lg md:text-xl text-neutral-500 max-w-2xl mx-auto mb-10 leading-relaxed">
            See the semantic steps users take in a session—and how conversations flow, loop, or end.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://dashboard.useturret.com"
              className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center gap-2 shadow-lg shadow-turret-purple/25 hover:shadow-xl hover:shadow-turret-purple/30 transition-all duration-300"
            >
              <span>Start free 14-day trial</span>
              <span aria-hidden="true" className="text-white">→</span>
            </a>
            <Link
              to="how-it-works"
              className="btn-secondary text-lg px-8 py-4 cursor-pointer"
              smooth={true}
              duration={300}
              offset={-80}
            >
              See how it works
            </Link>
          </div>
        </div>

        {/* Video demo container */}
        <div className="relative mx-auto max-w-5xl">
          {/* Decorative glow behind video */}
          <div className="absolute -inset-4 bg-gradient-to-r from-turret-purple/20 via-indigo-500/20 to-turret-purple-light/20 rounded-3xl blur-2xl opacity-60" />
          
          {/* Video frame */}
          <div className="relative rounded-2xl overflow-hidden bg-neutral-900 shadow-2xl ring-1 ring-white/10">
            {/* Browser chrome effect */}
            <div className="flex items-center gap-2 px-4 py-3 bg-neutral-800/80 border-b border-white/5">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-green-500/80" />
              </div>
              <div className="flex-1 flex justify-center">
                <div className="px-4 py-1 rounded-md bg-neutral-700/50 text-neutral-400 text-xs font-mono">
                  dashboard.useturret.com
                </div>
              </div>
              <div className="w-[52px]" /> {/* Spacer for symmetry */}
            </div>
            
            {/* 16:9 video wrapper */}
            <div className="relative w-full" style={{ paddingBottom: '62.67441860465116%' }}>
              <iframe
                src="https://www.loom.com/embed/128c67a88532454f846eb1e7d8ffb55e?hideEmbedTopBar=true"
                title="Turret conversation analytics demo"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
